/**
 * Line-based parser for admin-authored help text (requirement information
 * text and form-field descriptions). No markdown dependency and no HTML: the
 * only syntax is a marker at the start of a line.
 *
 * Rules, applied per line after trimming:
 *  - `- ` or `• `              → bullet list item
 *  - `1. `, `2. ` …            → numbered list item
 *  - `a. `, `b. ` … (lowercase) → letter list item (existing texts use this)
 *  - blank line                 → ends the current paragraph or list
 *  - anything else              → paragraph text; consecutive plain lines join
 *                                 into one paragraph with their line breaks kept
 *
 * `* ` is deliberately not a bullet marker: production texts use `*` and `**`
 * at the start of a line as footnote references.
 */

export type InfoTextListStyle = 'bullet' | 'number' | 'letter';

export type InfoTextBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'list'; style: InfoTextListStyle; items: string[] };

const LIST_MARKERS: ReadonlyArray<{ style: InfoTextListStyle; pattern: RegExp }> = [
  { style: 'bullet', pattern: /^[-•]\s+(.+)$/ },
  { style: 'number', pattern: /^\d{1,3}\.\s+(.+)$/ },
  { style: 'letter', pattern: /^[a-z]\.\s+(.+)$/ },
];

function matchListItem(line: string): { style: InfoTextListStyle; item: string } | null {
  for (const { style, pattern } of LIST_MARKERS) {
    const match = pattern.exec(line);
    if (match?.[1]) return { style, item: match[1].trim() };
  }
  return null;
}

/**
 * Splits help text into paragraphs and lists.
 *
 * @param text Raw text as stored, with `\n` (or `\r\n`) line breaks.
 * @returns Blocks in document order; empty for blank input.
 */
export function parseInfoText(text: string): InfoTextBlock[] {
  const blocks: InfoTextBlock[] = [];
  let current: InfoTextBlock | null = null;

  const flush = () => {
    if (current) blocks.push(current);
    current = null;
  };

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();

    if (line === '') {
      flush();
      continue;
    }

    const listItem = matchListItem(line);
    if (listItem) {
      if (current?.type === 'list' && current.style === listItem.style) {
        current.items.push(listItem.item);
      } else {
        flush();
        current = { type: 'list', style: listItem.style, items: [listItem.item] };
      }
      continue;
    }

    if (current?.type === 'paragraph') {
      current.text += `\n${line}`;
    } else {
      flush();
      current = { type: 'paragraph', text: line };
    }
  }

  flush();
  return blocks;
}
